import clsx from 'clsx';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { IconSelect } from '@/core/IconSelect.molecule';
import { SelectWorkbooks } from '@/core/SelectWorkbooks.molecule';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { useFlux } from '@/core/hooks/useFlux.hook';
import { KEY_CODES } from '@/main/app.constants';
import { ActiveFilters } from '@/search/ActiveFilters';
import { DatasourcePreferencesToggle } from '@/search/DatasourcePreferencesToggle.molecule';
import { clear, searchItems } from '@/search/search.actions';
import { SORT_BY_OPTIONS } from '@/search/search.constants';
import {
  useSearchActions,
  useSearchCurrentAsset,
  useSearchDatasourceFilter,
  useSearchDatasources,
  useSearchDescriptionFilter,
  useSearchIsAdvancedMode,
  useSearchIsExactName,
  useSearchIsUsingDatasourcePrefsSearchFilters,
  useSearchLocallyScoped,
  useSearchNameFilter,
  useSearchRecentDatasourceFilters,
  useSearchSearching,
  useSearchSortBy,
  useSearchTypeFilter,
  useSearchWorkbooksFilter,
} from '@/search/search.store';
import { getConflictingSearchTypes, hasInvalidSearchTypes, showOnlyTheseDatasources } from '@/search/search.utilities';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { focusAndSelectElement } from '@/utilities/dom.utilities';
import { getTypeTranslation } from '@/utilities/utilities';
import { setUserPreferencesDisplay, setUserPreferencesTab } from '@/workbench/workbench.actions';
import { PREFERENCE_TABS } from '@/workbench/workbench.store';
import { SeeqNames } from '@seeqdev/constants';
import { faAngleDown, faAngleUp, faSearch } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Button, Icon, Select, TextField } from '@seeqdev/qomponents';
import type { Datasource, SearchFiltersProps } from '@/search/search.types';
import type { SelectOptionIF } from '@/usage/Usage.page';
import type { AnyProperty } from '@/utilities.types';

const SEARCH_TYPES_TO_COMBINE: Record<string, string> = {
  [SeeqNames.Types.CalculatedCondition]: SeeqNames.Types.Condition,
  [SeeqNames.Types.StoredCondition]: SeeqNames.Types.Condition,
  [SeeqNames.Types.CalculatedSignal]: SeeqNames.Types.Signal,
  [SeeqNames.Types.StoredSignal]: SeeqNames.Types.Signal,
  [SeeqNames.Types.CalculatedScalar]: SeeqNames.Types.Scalar,
  [SeeqNames.Types.LiteralScalar]: SeeqNames.Types.Scalar,
};

const MIN_LENGTH_FOR_SHOWING_RECENT_DATASOURCES = 5;

/** This will prevent duplicate options when searching by item type */
const combineSearchTypes = (searchTypes: string[]) =>
  Array.from(
    new Set(
      searchTypes.map((type) => {
        const combinedType = SEARCH_TYPES_TO_COMBINE[type];

        return combinedType ? combinedType : type;
      }),
    ),
  );

/**
 * Make an array of objects the react-select can use.
 *
 * Construct the options list by calling omitOrIncludeUserDatasources, and also do not include anything
 * that is currently selected (check with 'datasourceFilter' from the flux store to know what is selected) so that
 * it does not appear in both the 'selected' area and the dropdown list.
 */
export const buildSelectOptions = (
  datasources: Datasource[],
  datasourceFilter: Datasource[],
  isUsingDatasourcePrefsSearchFilters: boolean,
) => {
  const processDatasources = (dataSources: Datasource[]) => {
    return _.chain(dataSources)
      .map((datasource) => ({
        label: datasource.name,
        value: datasource,
      }))
      .sortBy((datasource) => datasource.label?.toLowerCase())
      .value();
  };

  if (isUsingDatasourcePrefsSearchFilters) {
    const filteredDatasources = showOnlyTheseDatasources(
      sqWorkbenchStore.stateParams.workbookId,
      datasources,
      sqWorkbenchStore.userDatasources,
      { includeNewDatasources: sqWorkbenchStore.includeNewDatasources },
    );
    const unselectedOnly = filteredDatasources.filter(
      (datasource) => !datasourceFilter.some((selected) => selected.id === datasource.id),
    );
    return processDatasources(unselectedOnly);
  } else {
    return processDatasources(datasources);
  }
};

export const SearchFilters = (props: SearchFiltersProps) => {
  const { pane, scopeIds, excludeGloballyScoped = false, filterByWorkbook } = props;
  const searchTypes = combineSearchTypes(props.searchTypes);
  const { isTopic } = useFlux(sqWorkbookStore);
  const { t } = useTranslation();

  const nameFilterRef = useRef<HTMLInputElement>(null);

  const focusNameInput = () => nameFilterRef.current?.focus();
  useEffect(focusNameInput, []);

  const nameFilter = useSearchNameFilter();
  const workbooksFilter = useSearchWorkbooksFilter();
  const descriptionFilter = useSearchDescriptionFilter();
  const searching = useSearchSearching();
  const currentAsset = useSearchCurrentAsset();
  const typeFilter = useSearchTypeFilter();
  const datasourceFilter = useSearchDatasourceFilter();
  const sortBy = useSearchSortBy();
  const isAdvancedMode = useSearchIsAdvancedMode();
  const datasources = useSearchDatasources();
  const recentDatasourceFilters = useSearchRecentDatasourceFilters();
  const isExactName = useSearchIsExactName();
  const searchLocallyScoped = useSearchLocallyScoped();
  const isUsingDatasourcePrefsSearchFilters = useSearchIsUsingDatasourcePrefsSearchFilters();
  const {
    setDescriptionFilter,
    setIsAdvancedMode,
    setIsExactName,
    setIsUsingDatasourcePrefsSearchFilters,
    setNameFilter,
    setSearchLocallyScoped,
    setSelectedDatasources,
    setSelectedTypes,
    setSortBy,
    setWorkbooksFilter,
  } = useSearchActions();

  const [searchValue, setSearchValue] = useState(nameFilter);
  const [descriptionSearch, setDescriptionSearch] = useState(descriptionFilter);

  useEffect(() => {
    setSearchValue(nameFilter);
    setDescriptionSearch(descriptionFilter);
  }, [nameFilter, descriptionFilter]);

  const search = async () => {
    setNameFilter(searchValue);
    setDescriptionFilter(descriptionSearch);

    const scopedTo =
      workbooksFilter.length > 0 ? (scopeIds ?? []).concat(workbooksFilter.map(({ id }) => id)) : scopeIds;
    await searchItems(pane, searchTypes, scopedTo, excludeGloballyScoped || filterByWorkbook || searchLocallyScoped);

    const searchSummary: AnyProperty<string | number | boolean> = {
      advancedMode: isAdvancedMode,
      nameSpecified: !!nameFilter,
      descriptionSpecified: !!descriptionFilter,
      assetSpecified: !!currentAsset,
      datasourceFilterCount: _.size(datasourceFilter),
      sortBy,
    };
    searchTypes.forEach((searchType) => {
      searchSummary[`typeFilter-${searchType}`] = typeFilter.includes(searchType);
    });

    doTrack(TRACK.SEARCH, 'Search Complete', JSON.stringify(searchSummary));
  };

  /**
   * Using "searchTypes", make an array of objects the react-select can use.
   */
  const searchTypesOptions = searchTypes.map((type) => ({
    label: t(getTypeTranslation(type)),
    value: type,
  }));

  const datasourcesOptions = buildSelectOptions(datasources, datasourceFilter, isUsingDatasourcePrefsSearchFilters);
  const recentDatasourcesOptions = buildSelectOptions(
    recentDatasourceFilters,
    datasourceFilter,
    isUsingDatasourcePrefsSearchFilters,
  );

  /**
   * If there are more than a certain number of datasources, show a "Recent" section in the Datasources dropdown.
   * Ensure "recent" only contains datasources that actually still exist (ie: in case of deletion).
   * Filter out anything that is currently selected.
   * */
  const getDatasourceOptions = () => {
    if (datasourcesOptions.length >= MIN_LENGTH_FOR_SHOWING_RECENT_DATASOURCES) {
      return [
        {
          label: t('SEARCH_DATA.RECENT_DATASOURCE_FILTER_OPTIONS'),
          options: recentDatasourcesOptions.filter((recentOption) =>
            datasourcesOptions.some((datasourceOption) => datasourceOption.value.id === recentOption.value.id),
          ),
        },
        {
          label: isUsingDatasourcePrefsSearchFilters
            ? t('SEARCH_DATA.ALL_DATASOURCE_PREFERRED_ONLY')
            : t('SEARCH_DATA.ALL_DATASOURCE_FILTER_OPTIONS'),
          options: datasourcesOptions.filter(
            (datasourceOption) =>
              !recentDatasourcesOptions.some((recentOption) => recentOption.value.id === datasourceOption.value.id),
          ),
        },
      ];
    } else {
      return [
        {
          label: isUsingDatasourcePrefsSearchFilters
            ? t('SEARCH_DATA.ALL_DATASOURCE_PREFERRED_ONLY')
            : t('SEARCH_DATA.ALL_DATASOURCE_FILTER_OPTIONS'),
          options: datasourcesOptions,
        },
      ];
    }
  };

  const activeFilters = [
    { label: 'Description', filter: descriptionFilter, onClear: () => setDescriptionFilter('') },
    { label: 'Type', filter: typeFilter, onClear: () => setSelectedTypes([]) },
    { label: 'Datasource', filter: datasourceFilter, onClear: () => setSelectedDatasources([]) },
    { label: 'Sort', filter: sortBy, onClear: () => setSortBy('') },
  ];

  const hasTypesError = hasInvalidSearchTypes(typeFilter);
  const conflictingTypeNames = getConflictingSearchTypes(typeFilter)
    .map((type) => t(getTypeTranslation(type)))
    .join(', ');

  return (
    <div
      data-testid="searchFilters"
      className={clsx('card card-default', {
        borderGray: pane === 'modal',
        mt5: pane !== 'modal',
      })}>
      <div className="tw:min-h-0.5 tw:px-2 tw:py-4" data-testid="searchPanel">
        <TextField
          extraClassNames="mb3 width-maximum"
          id="searchInput"
          name="nameFilter"
          testId="nameFilter"
          placeholder={t('SEARCH_DATA.NAME_CONTAINS')}
          value={searchValue}
          onFocus={focusAndSelectElement}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyUp={(e) => e.keyCode === KEY_CODES.ENTER && search()}
          ref={nameFilterRef}
        />

        {filterByWorkbook && (
          <SelectWorkbooks
            testId="workbooksFilter"
            value={workbooksFilter}
            onChange={(workbooks) => setWorkbooksFilter(workbooks)}
            notSelectedMessage="ITEM_FINDER.NO_WORKBENCH_SELECTED"
            isMultiple={true}
            title="Select Workbooks"
            extraClassNames="mtb10"
          />
        )}

        <Checkbox
          label="EXACT_MATCH_CHECKBOX"
          isChecked={isExactName}
          classes="mt5 mb5"
          id="EXACT_MATCH_CHECKBOX"
          onChange={() => setIsExactName(!isExactName)}
        />

        {!isAdvancedMode && <ActiveFilters selectedFilters={activeFilters} actionAfterFilterCleared={search} />}

        {isAdvancedMode && (
          <div className="advancedFormWrapper">
            <Checkbox
              label={isTopic ? 'SEARCH_LOCALLY_SCOPED' : 'SEARCH_LOCALLY_SCOPED_WORKBOOK'}
              isChecked={searchLocallyScoped}
              classes="mb5"
              id={isTopic ? 'SEARCH_LOCALLY_SCOPED' : 'SEARCH_LOCALLY_SCOPED_WORKBOOK'}
              tooltip={isTopic ? 'SEARCH_LINKED_WORKBOOKS_TOOLTIP' : 'SEARCH_LOCALLY_SCOPED_WORKBOOK_TOOLTIP'}
              onChange={() => setSearchLocallyScoped(!searchLocallyScoped)}
              isTooltipSimple={true}
            />

            <TextField
              extraClassNames="mb10 width-maximum"
              id="searchInput"
              name="descriptionFilter"
              testId="descriptionFilter"
              placeholder={t('SEARCH_DATA.DESCRIPTION_CONTAINS')}
              value={descriptionSearch}
              onFocus={focusAndSelectElement}
              onKeyUp={(e) => e.keyCode === KEY_CODES.ENTER && search()}
              onChange={(e) => setDescriptionSearch(e.target.value)}
            />
            {/* Item type filter */}
            <div data-testid="typeFilter" className="multiSelectTwoLine tw:mb-sq-10">
              <Select
                isMulti={true}
                isClearable={true}
                closeMenuOnSelect={false}
                isSearchable={true}
                options={searchTypesOptions}
                menuPortalTarget={document.body}
                placeholder={t('SEARCH_DATA.ALL_TYPES')}
                value={typeFilter.map((type) => ({ label: t(getTypeTranslation(type)), value: type }))}
                onChange={(selectedTypes: SelectOptionIF<string>[]) => {
                  setSelectedTypes(selectedTypes);
                }}
                showError={hasTypesError}
              />

              {hasTypesError && (
                <span className="sq-text-danger">
                  {t('SEARCH_DATA.TYPES_ERROR_V2', { types: conflictingTypeNames })}
                </span>
              )}
            </div>

            {/* Datasource filter */}
            <DatasourcePreferencesToggle
              on={isUsingDatasourcePrefsSearchFilters}
              onChange={() => setIsUsingDatasourcePrefsSearchFilters(!isUsingDatasourcePrefsSearchFilters)}
              formattedLabel={t('SEARCH_DATA.USE_DATASOURCE_PREFERENCES')}
              onClick={() => {
                setUserPreferencesTab(PREFERENCE_TABS.DATASOURCES);
                setUserPreferencesDisplay(true);
              }}
            />
            <div data-testid="datasourceFilter" className="multiSelectLarge tw:mb-sq-10">
              <Select
                isMulti={true}
                isClearable={true}
                closeMenuOnSelect={false}
                isSearchable={true}
                options={getDatasourceOptions()}
                menuPortalTarget={document.body}
                placeholder={
                  isUsingDatasourcePrefsSearchFilters
                    ? t('SEARCH_DATA.ALL_DATASOURCE_PREFERRED_ONLY')
                    : t('SEARCH_DATA.ALL_DATASOURCE_FILTER_OPTIONS')
                }
                value={datasourceFilter.map((datasource) => ({
                  label: datasource.name,
                  value: datasource,
                }))}
                onChange={(selectedDatasources: SelectOptionIF<Datasource>[]) => {
                  setSelectedDatasources(selectedDatasources);
                }}
              />
            </div>

            <IconSelect
              name="sortByFilter"
              value={sortBy}
              wrapperClasses="mb10"
              selectOptions={_.map(SORT_BY_OPTIONS, (option, key) => ({
                text: `SEARCH_DATA.SORT_BY_OPTIONS.${key}`,
                value: option,
              }))}
              onChange={(sort) => setSortBy(_.toString(sort.value))}
            />
          </div>
        )}

        <div className="flexColumnContainer tw:justify-between">
          <Button
            label={t('SEARCH_DATA.RESET')}
            onClick={() => {
              setSearchValue('');
              setDescriptionSearch('');
              clear(pane, searchTypes, false, scopeIds, excludeGloballyScoped);
              nameFilterRef.current?.focus();
            }}
            testId="resetButton"
            extraClassNames="height-fit-content"
          />
          <div className="flexColumnContainer tw:flex-wrap-reverse tw:items-center tw:justify-end tw:gap-sq-10">
            <a
              className="force-link-color cursorPointer"
              data-testid="moreFiltersButton"
              onClick={() => setIsAdvancedMode(!isAdvancedMode)}>
              {t('SEARCH_DATA.FILTER_AND_SORT')}
              <Icon icon={isAdvancedMode ? faAngleUp : faAngleDown} extraClassNames="ml2" />
            </a>
            <Button
              testId="searchButton"
              id="spec-search-btn"
              label={t('SEARCH')}
              variant="theme"
              icon={faSearch}
              inProgress={Boolean(searching)}
              disabled={Boolean(searching) || hasTypesError}
              iconStyle="white"
              type="submit"
              onClick={search}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
